import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useState } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AISources, useAnalytics } from '../../common';
import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics';
import { AIAnswerBody } from '../ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerFooter } from '../ai-answer-footer';
import { AIAnswerLoadingDialog } from '../ai-answer-loading/ai-answer-loading-dialog';
import { aiAnswerCollapseClicked, aiAnswerExpandClicked, aiAnswerExpandShown, onAIAnswerSourceClick } from '../analytics';
import { NLPSearchResultFormat } from '../types';
import { Collapse } from './collapse';
import { AIAnswerSearchBorder } from './styled';
var MAX_SOURCES = 4;
var spacerStyles = xcss({
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  lineHeight: '14px',
  paddingTop: 'space.250'
});
var errorContainerStyles = xcss({
  marginTop: 'space.100',
  marginBottom: 'space.200'
});
var sourcesContainerStyles = xcss({
  paddingTop: '0',
  paddingRight: 'space.300',
  paddingBottom: 'space.100',
  paddingLeft: 'space.300'
});
export var LegacySmartAnswerCard = function LegacySmartAnswerCard(_ref) {
  var LoadingWrapper = _ref.LoadingWrapper,
    DialogWrapper = _ref.DialogWrapper;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    callbacks = _useAIAnswerContext.callbacks,
    loading = _useAIAnswerContext.loading,
    isReadingAids = _useAIAnswerContext.isReadingAids,
    onClose = _useAIAnswerContext.onClose,
    hasError = _useAIAnswerContext.hasError;
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIResultAction = _useAIEventsInstrumen.trackAIResultAction;
  var query = commonAttributes.query;
  var answerFormat = commonAttributes.answerFormat;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    collapsed = _useState2[0],
    setCollapsed = _useState2[1];
  var isQuickSearch = Boolean(!isReadingAids && onClose);

  // reset on each new query
  useEffect(function () {
    setCollapsed(true);
  }, [query]);
  var toggleCollapsedState = function toggleCollapsedState() {
    if (collapsed) {
      fireSAINUsageEvent({
        metCriteria: SainAction.EXPAND_CLICKED,
        commonAttributes: commonAttributes
      });
    }
    setCollapsed(!collapsed);
  };
  var analyticsCallbacks = {
    onExpandedByDefault: function onExpandedByDefault() {
      return fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes));
    },
    onExpandClicked: function onExpandClicked() {
      return fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
    },
    onCollapseClicked: function onCollapseClicked() {
      return fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
    }
  };
  var sourceClickCallback = useCallback(function (index, hostname) {
    var _callbacks$onAIAnswer;
    fireAnalyticsEvent(onAIAnswerSourceClick(_objectSpread({
      sourceIndex: index,
      hostname: hostname
    }, commonAttributes)));
    fg('kd_enable_new_usage_tracking') ? fireSAINUsageEvent({
      metCriteria: SainAction.SOURCE_CLICKED,
      commonAttributes: commonAttributes
    }) : trackAIResultAction(SainAction.SOURCE_CLICKED);
    callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer = callbacks.onAIAnswerSourceClick) === null || _callbacks$onAIAnswer === void 0 || _callbacks$onAIAnswer.call(callbacks);
  }, [fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks, trackAIResultAction]);
  if (loading) {
    return /*#__PURE__*/React.createElement(LoadingWrapper, null, /*#__PURE__*/React.createElement(AIAnswerLoadingDialog, null));
  }
  var aiAnswerContent = /*#__PURE__*/React.createElement(Collapse, {
    collapsed: collapsed,
    toggleCollapsedState: toggleCollapsedState,
    expandWhenClickedAnywhere: true,
    isLoading: false,
    callbacks: analyticsCallbacks
  }, /*#__PURE__*/React.createElement(AIAnswerBody, null));
  if (hasError) {
    aiAnswerContent = /*#__PURE__*/React.createElement(Box, {
      xcss: errorContainerStyles
    }, /*#__PURE__*/React.createElement(AIAnswerBody, null));
  } else if (isQuickSearch || answerFormat === NLPSearchResultFormat.JSON) {
    aiAnswerContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      xcss: spacerStyles
    }), /*#__PURE__*/React.createElement(AIAnswerBody, null));
  }
  return /*#__PURE__*/React.createElement(DialogWrapper, null, /*#__PURE__*/React.createElement(AIAnswerSearchBorder, {
    isLoading: false,
    isStreaming: false
  }, aiAnswerContent, !hasError && /*#__PURE__*/React.createElement(Box, {
    xcss: sourcesContainerStyles
  }, /*#__PURE__*/React.createElement(AISources, {
    sources: commonAttributes.sources,
    searchSessionId: commonAttributes.searchSessionId,
    onClickHandler: sourceClickCallback,
    limit: MAX_SOURCES,
    enableSmartLinkSources: true
  })), /*#__PURE__*/React.createElement(AIAnswerFooter, null)));
};